import { DealType } from '../../enums/DealType';
import { FilterItemResource } from '../../types/FilterTypes';
import { NoteResource } from '../../types/Note';
import {
  defaultStatus,
  initialBpsValue,
  initialDealScoreValue,
  initialInMarketScoreValue,
  initialLastUpdated,
  initialVehicleScoreValue,
  PortfolioFilterResource,
  PortfolioSelectedFilterResource,
} from '../../types/PortfolioFilterResource';
import { PortfolioView } from '../../types/PortfolioView';
import { PrivateIncentiveModalResource } from '../../types/PrivateIncentiveModalResource';
import { PortfolioActions, PortfolioActionsType } from './PortfolioActions';

export type PortfolioState = {
  allView: PortfolioView;
  leaseView: PortfolioView;
  cashView: PortfolioView;
  financeView: PortfolioView;
  isLoading: boolean;
  dealType: string;
  currentStoreId: string;
  portfolioOptions: Array<FilterItemResource>;
  isPortfolioBulkPrintInPorgress: boolean;
  isPortfolioBulkExportInPorgress: boolean;
  isPortfolioFilterReset: boolean;
  openFilterCount: number;
  isNotesModalOpen: boolean;
  notesModalResource: NoteResource;
  isPrivateIncentiveModalOpen: boolean;
  privateIncentiveModalResource: PrivateIncentiveModalResource;
  leftMobileMenu: boolean;
  isPortfolioResetPaginator: boolean;
};

const initialFilters: PortfolioFilterResource = {
  assignees: [],
  maxBps: 100,
  minBps: 0,
  maxInMarketScore: 100,
  minInMarketScore: 0,
  maxVehicleScore: 100,
  minVehicleScore: 0,
  maxDealScore: 100,
  minDealScore: 0,
  dealTypes: [],
  maxDate: new Date().toISOString(),
  minDate: new Date().toISOString(),
  maxAgeDate: new Date().toISOString(),
  minAgeDate: new Date().toISOString(),
  soldBy: [],
  colors: [],
  optOutPreference: [],
  currentVehicleModels: [],
  currentVehicleTrims: [],
  currentVehicleYears: [],
  currentVehicleTypes: [],
  replacementVehicleSelection: [],
  currentVehicleMakes: [],
  incentives: [],
  replacementVehicleModels: [],
  replacementVehicleTrims: [],
  replacementVehicleYears: [],
  replacementVehicleTypes: [],
  replacementVehicleMakes: [],
  insights: [],
  status: [],
  recall: [],
  campaignActivityViewCounts: [],
  campaignExpiryDates: [],
  fordIncentiveNames: [],
  shopperActivity: [],
};

const initialSelectedFilters: PortfolioSelectedFilterResource = {
  assignees: [],
  bpsValue: initialBpsValue,
  inMarketScore: initialInMarketScoreValue,
  vehicleScore: initialVehicleScoreValue,
  dealScore: initialDealScoreValue,
  dealTypes: [],
  lastUpdated: initialLastUpdated,
  soldBy: [],
  colors: [],
  optOutPreference: [],
  currentVehicleModels: [],
  currentVehicleTrims: [],
  currentVehicleYears: [],
  currentVehicleMakes: [],
  currentVehicleTypes: null,
  replacementVehicleSelection: null,
  currentVehicleRate: null,
  incentives: [],
  replacementVehicleModels: [],
  replacementVehicleTrims: [],
  replacementVehicleYears: [],
  replacementVehicleTypes: [],
  replacementVehicleMakes: [],
  replacementVehicleMileage: null,
  replacementVehicleRate: null,
  paymentsRemaining: null,
  isTrimConfirmationRequired: null,
  currentVehicleMileage: null,
  monthsOfOwnership: null,
  insights: [],
  ageDays: null,
  status: defaultStatus,
  lastServiceDate: null,
  recall: [],
  campaignActivityViewCounts: [],
  campaignExpiryDates: [],
  fordIncentiveNames: [],
  shopperActivity: { key: 'None', label: 'None' },
};

export const initialPortfolioState: PortfolioState = {
  allView: {
    portfolioRequest: null,
    totalRecordsCount: 0,
    shouldRefresh: false,
    shouldRefreshDealCount: false,
    isFilterChanged: false,
    defaultBpsValue: initialBpsValue,
    defaultInMarketScoreValue: initialInMarketScoreValue,
    defaultVehicleScoreValue: initialVehicleScoreValue,
    defaultDealScoreValue: initialDealScoreValue,
    defaultLastUpdated: initialLastUpdated,
    filters: initialFilters,
    selectedFilters: initialSelectedFilters,
    masterFilterKey: null,
    notesCountMap: [],
  },
  leaseView: {
    portfolioRequest: null,
    totalRecordsCount: 0,
    shouldRefresh: false,
    shouldRefreshDealCount: false,
    isFilterChanged: false,
    defaultBpsValue: initialBpsValue,
    defaultInMarketScoreValue: initialInMarketScoreValue,
    defaultVehicleScoreValue: initialVehicleScoreValue,
    defaultDealScoreValue: initialDealScoreValue,
    defaultLastUpdated: initialLastUpdated,
    filters: initialFilters,
    selectedFilters: initialSelectedFilters,
    masterFilterKey: null,
    notesCountMap: [],
  },
  cashView: {
    portfolioRequest: null,
    totalRecordsCount: 0,
    shouldRefresh: false,
    shouldRefreshDealCount: false,
    isFilterChanged: false,
    defaultBpsValue: initialBpsValue,
    defaultInMarketScoreValue: initialInMarketScoreValue,
    defaultVehicleScoreValue: initialVehicleScoreValue,
    defaultDealScoreValue: initialDealScoreValue,
    defaultLastUpdated: initialLastUpdated,
    filters: initialFilters,
    selectedFilters: initialSelectedFilters,
    masterFilterKey: null,
    notesCountMap: [],
  },
  financeView: {
    portfolioRequest: null,
    totalRecordsCount: 0,
    shouldRefresh: false,
    shouldRefreshDealCount: false,
    isFilterChanged: false,
    defaultBpsValue: initialBpsValue,
    defaultInMarketScoreValue: initialInMarketScoreValue,
    defaultVehicleScoreValue: initialVehicleScoreValue,
    defaultDealScoreValue: initialDealScoreValue,
    defaultLastUpdated: initialLastUpdated,
    filters: initialFilters,
    selectedFilters: initialSelectedFilters,
    masterFilterKey: null,
    notesCountMap: [],
  },
  isLoading: false,
  dealType: null,
  currentStoreId: null,
  portfolioOptions: [],
  isPortfolioBulkPrintInPorgress: false,
  isPortfolioBulkExportInPorgress: false,
  isPortfolioFilterReset: false,
  openFilterCount: 0,
  isNotesModalOpen: false,
  notesModalResource: {
    dealerId: '',
    fullName: '',
    closedDealId: '',
    vin: '',
    status: '',
    subStatus: '',
  },
  isPrivateIncentiveModalOpen: false,
  privateIncentiveModalResource: {
    lastName: '',
    vin: '',
    isLeaseBuyOut: false,
    leadId: '',
    legacyDealerId: '',
    privateIncentiveLookupActionType: '',
    leadDealerId: '',
  },
  leftMobileMenu: false,
  isPortfolioResetPaginator: false,
};

export const portfolioReducer = (
  state: PortfolioState = initialPortfolioState,
  action: PortfolioActions,
): PortfolioState => {
  switch (action.type) {
    case PortfolioActionsType.SET_PORTFOLIO_REQUEST_RESOURCE: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              portfolioRequest: action.payload.requestBody,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              portfolioRequest: action.payload.requestBody,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              portfolioRequest: action.payload.requestBody,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              portfolioRequest: action.payload.requestBody,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              portfolioRequest: action.payload.requestBody,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_DATA_TOTAL_COUNT: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              totalRecordsCount: action.payload.value,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              totalRecordsCount: action.payload.value,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              totalRecordsCount: action.payload.value,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              totalRecordsCount: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              totalRecordsCount: action.payload.value,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_SHOULD_REFRESH: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              shouldRefresh: action.payload.value,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              shouldRefresh: action.payload.value,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              shouldRefresh: action.payload.value,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              shouldRefresh: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              shouldRefresh: action.payload.value,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_SHOULD_REFRESH_DEAL_COUNT: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              shouldRefreshDealCount: action.payload.value,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              shouldRefreshDealCount: action.payload.value,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              shouldRefreshDealCount: action.payload.value,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              shouldRefreshDealCount: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              shouldRefreshDealCount: action.payload.value,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_IS_LOADING: {
      return {
        ...state,
        isLoading: action.payload,
      };
    }

    case PortfolioActionsType.SET_IS_PORTFOLIO_FILTERS_CHANGED: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              isFilterChanged: action.payload.value,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              isFilterChanged: action.payload.value,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              isFilterChanged: action.payload.value,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              isFilterChanged: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              isFilterChanged: action.payload.value,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_MASTER_FILTER: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              masterFilterKey: action.payload.key,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              masterFilterKey: action.payload.key,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              masterFilterKey: action.payload.key,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              masterFilterKey: action.payload.key,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              masterFilterKey: action.payload.key,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_FILTERS: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              filters: action.payload.filters,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              filters: action.payload.filters,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              filters: action.payload.filters,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              filters: action.payload.filters,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              filters: action.payload.filters,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_SELECTED_FILTERS: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              selectedFilters: action.payload.filters,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              selectedFilters: action.payload.filters,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              selectedFilters: action.payload.filters,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              selectedFilters: action.payload.filters,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              selectedFilters: action.payload.filters,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_DEFAULT_BPS_VALUE: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              defaultBpsValue: action.payload.value,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              defaultBpsValue: action.payload.value,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              defaultBpsValue: action.payload.value,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              defaultBpsValue: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              defaultBpsValue: action.payload.value,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_DEFAULT_LAST_UPDATED: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              defaultLastUpdated: action.payload.value,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              defaultLastUpdated: action.payload.value,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              defaultLastUpdated: action.payload.value,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              defaultLastUpdated: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              defaultLastUpdated: action.payload.value,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_CURRENT_DEAL_TYPE: {
      return {
        ...state,
        dealType: action.payload,
      };
    }

    case PortfolioActionsType.SET_PORTFOLIO_OPTIONS: {
      return {
        ...state,
        portfolioOptions: action.payload,
      };
    }

    case PortfolioActionsType.SET_PORTFOLIO_CURRENT_STORE_ID: {
      return {
        ...state,
        currentStoreId: action.payload,
      };
    }

    case PortfolioActionsType.SET_PORTFOLIO_UPDATED_NOTES_COUNT: {
      const dealType = action.payload.dealType.toLowerCase();

      switch (dealType) {
        case DealType.Lease.toLowerCase():
          const currentLeaseNotesCount = state.leaseView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentLeaseNotesCount.count = action.payload.value.count;

          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              notesCountMap: state.leaseView.notesCountMap,
            },
          };

        case DealType.Finance.toLowerCase():
          const currentFinanceNotesCount = state.financeView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentFinanceNotesCount.count = action.payload.value.count;
          return {
            ...state,
            financeView: {
              ...state.financeView,
              notesCountMap: state.financeView.notesCountMap,
            },
          };

        case DealType.Cash.toLowerCase():
          const currentCashNotesCount = state.cashView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentCashNotesCount.count = action.payload.value.count;
          return {
            ...state,
            cashView: {
              ...state.cashView,
              notesCountMap: state.cashView.notesCountMap,
            },
          };

        case 'all':
          const currentAllNotesCount = state.allView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentAllNotesCount.count = action.payload.value.count;
          return {
            ...state,
            allView: {
              ...state.allView,
              notesCountMap: state.allView.notesCountMap,
            },
          };

        default: {
          const currentDefaultNotesCount = state.leaseView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentDefaultNotesCount.count = action.payload.value.count;
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              notesCountMap: state.leaseView.notesCountMap,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_PORTFOLIO_NOTES_COUNT_MAP: {
      const dealType = action.payload.dealType.toLowerCase();
      switch (dealType) {
        case DealType.Lease.toLowerCase():
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              notesCountMap: action.payload.value,
            },
          };

        case DealType.Finance.toLowerCase():
          return {
            ...state,
            financeView: {
              ...state.financeView,
              notesCountMap: action.payload.value,
            },
          };

        case DealType.Cash.toLowerCase():
          return {
            ...state,
            cashView: {
              ...state.cashView,
              notesCountMap: action.payload.value,
            },
          };

        case 'all':
          return {
            ...state,
            allView: {
              ...state.allView,
              notesCountMap: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            leaseView: {
              ...state.leaseView,
              notesCountMap: action.payload.value,
            },
          };
        }
      }
    }

    case PortfolioActionsType.SET_IS_PORTFOLIO_BULKPRINT_INPROGRESS: {
      return {
        ...state,
        isPortfolioBulkPrintInPorgress: action.payload,
      };
    }

    case PortfolioActionsType.SET_IS_PORTFOLIO_BULKEXPORT_INPROGRESS: {
      return {
        ...state,
        isPortfolioBulkExportInPorgress: action.payload,
      };
    }

    case PortfolioActionsType.SET_IS_PORTFOLIO_FILTER_RESET: {
      return {
        ...state,
        isPortfolioFilterReset: action.payload,
      };
    }

    case PortfolioActionsType.SET_PORTFOLIO_NOTES_MODAL_OPEN: {
      return {
        ...state,
        isNotesModalOpen: action.payload,
      };
    }

    case PortfolioActionsType.SET_PORTFOLIO_MODAL_RESOURCE: {
      return {
        ...state,
        notesModalResource: action.payload,
      };
    }

    case PortfolioActionsType.SET_IS_PORTFOLIO_FILTER_RESET: {
      return {
        ...state,
        isPortfolioFilterReset: action.payload,
      };
    }

    case PortfolioActionsType.SET_OPEN_FILTER_COUNT: {
      return {
        ...state,
        openFilterCount: action.payload,
      };
    }

    case PortfolioActionsType.SET_PRIVATE_INCENTIVE_MODAL_OPEN: {
      return {
        ...state,
        isPrivateIncentiveModalOpen: action.payload,
      };
    }

    case PortfolioActionsType.SET_PRIVATE_INCENTIVE_MODAL_RESOURCE: {
      return {
        ...state,
        privateIncentiveModalResource: action.payload,
      };
    }

    case PortfolioActionsType.SET_LEFT_MOBILE_MENU: {
      return {
        ...state,
        leftMobileMenu: action.payload,
      };
    }

    case PortfolioActionsType.SET_PORTFOLIO_ResetPaginator: {
      return {
        ...state,
        isPortfolioResetPaginator: action.payload,
      };
    }

    default:
      return state;
  }
};
